import { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { SPIRIT_CATEGORIES } from "@/data/spirits";
import { Search, Star, ArrowRight, X, BookOpen, ShoppingBag } from "lucide-react";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import DrinkImage from "@/components/DrinkImage";
import { getRetailerUrl } from "@/data/retailers";
import { getProductImage } from "@/data/productImages";

const BUDGET_LABEL = {
  "under-500": "Approx. under ₹500",
  "500-1000": "Approx. ₹500 – ₹1,000",
  "1000-2000": "Approx. ₹1,000 – ₹2,000",
  "2000-plus": "Approx. ₹2,000+",
};

export default function SpiritCatalog() {
  const { pathname } = useLocation();
  const category = pathname.replace("/", "").split("/")[0];
  const data = SPIRIT_CATEGORIES[category];
  const [q, setQ] = useState("");
  const [style, setStyle] = useState("All");

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.items.filter(it => {
      if (style !== "All" && it.style !== style) return false;
      if (q && !it.name.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [data, q, style]);

  if (!data) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <p className="font-serif-display text-3xl">Category not found.</p>
        <Link to="/" className="btn-pill btn-pink mt-6 inline-flex">Back home</Link>
      </div>
    );
  }

  return (
    <div className="bg-ink" data-testid={`catalog-${data.slug}`}>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={data.hero_image} alt={data.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/20" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16 sm:pt-32 sm:pb-24 text-cream">
          <p className="eyebrow" style={{ color: "#F9C0D2" }}>{data.eyebrow}</p>
          <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.02] max-w-3xl">
            {data.title}<br /><span className="italic font-serif-editor text-pink-brand font-medium">{data.tagline}</span>
          </h1>
          <p className="mt-5 max-w-2xl text-cream/80 font-body text-base sm:text-lg leading-relaxed">{data.intro}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={`/learn/${data.slug}-101`} className="btn-pill btn-pink" data-testid={`catalog-learn-${data.slug}`}>
              Read {data.title} 101 <ArrowRight size={16} />
            </Link>
            <Link to="/drink-finder" className="btn-pill btn-outline-cream" data-testid="catalog-drink-finder">
              Take the Drink Finder
            </Link>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="flex flex-col sm:flex-row gap-3 items-stretch">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-cream/40" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={`Search ${data.title.toLowerCase()}…`}
              className="w-full h-12 pl-11 pr-11 rounded-full bg-ink-panel border border-cream/10 focus:outline-none focus:border-pink-brand text-cream font-body"
              data-testid={`${data.slug}-search`}
            />
            {q && (
              <button onClick={() => setQ("")} className="absolute right-4 top-1/2 -translate-y-1/2 text-cream/40 hover:text-cream"><X size={16} /></button>
            )}
          </div>
          <div className="overflow-x-auto no-scrollbar">
            <div className="flex gap-2 min-w-max">
              {data.filters.map(f => (
                <button
                  key={f}
                  onClick={() => setStyle(f)}
                  className={`h-12 px-5 rounded-full text-sm font-semibold border transition-colors ${style === f ? "bg-ink text-cream border-ink" : "bg-ink-panel text-cream border-cream/10 hover:border-cream/30"}`}
                  data-testid={`${data.slug}-filter-${f.toLowerCase()}`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
        </div>

        <p className="mt-6 text-sm text-cream/50 font-body">{filtered.length} bottle{filtered.length !== 1 ? "s" : ""}</p>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 sm:gap-6">
          {filtered.map(it => {
            const productImage = it.image || getProductImage(it.id);
            return (
            <Dialog key={it.id}>
              <DialogTrigger asChild>
                <button className="group card-cream overflow-hidden text-left w-full flex flex-col" data-testid={`${data.slug}-card-${it.id}`}>
                  <div className="aspect-[4/3] relative overflow-hidden bg-ink-2">
                    <DrinkImage
                      src={productImage}
                      category={data.slug}
                      style={it.style}
                      name={it.name}
                      brand={it.brand}
                      alt={`${it.name} — ${it.style}${it.origin ? ` from ${it.origin}` : ""}`}
                    />
                    <div className="absolute top-3 left-3 chip chip-pink bg-pink text-cream border-transparent">{it.style}</div>
                  </div>
                  <div className="p-5 flex-1 flex flex-col gap-3">
                    <div>
                      <p className="font-serif-display text-xl font-bold text-cream leading-tight group-hover:text-pink-brand transition-colors">{it.name}</p>
                      <p className="text-xs text-cream/60 mt-1">{it.brand ? `${it.brand} · ` : ""}{it.origin}</p>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {it.notes.slice(0, 3).map(n => <span key={n} className="chip chip-ink text-[10px]">{n}</span>)}
                    </div>
                    <div className="mt-auto pt-2 flex items-center justify-between">
                      <span className="flex items-center gap-1 text-pink-brand text-xs font-semibold">
                        <Star size={12} fill="currentColor" /> {it.beginner_rating}/5 Editor's
                      </span>
                      <span
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-cream/70 group-hover:text-pink-brand"
                        data-testid={`${data.slug}-explore-${it.id}`}
                      >
                        <BookOpen size={12} /> View details
                      </span>
                    </div>
                  </div>
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-xl bg-ink-panel border-cream/10 max-h-[90vh] overflow-y-auto p-0 gap-0 block" data-testid={`${data.slug}-modal-${it.id}`}>
                <div className="flex flex-col">
                  <div className="w-full aspect-[16/10] overflow-hidden bg-ink-2 flex-shrink-0">
                    <DrinkImage
                      src={productImage}
                      category={data.slug}
                      style={it.style}
                      name={it.name}
                      brand={it.brand}
                      alt={`${it.name} — ${it.style}${it.origin ? ` from ${it.origin}` : ""}`}
                    />
                  </div>
                  <div className="p-6 sm:p-7 space-y-5">
                  {/* NAME */}
                  <div>
                    <h2 className="font-serif-display text-3xl font-bold text-cream leading-tight">{it.name}</h2>
                    {/* BRAND / CATEGORY */}
                    <p className="mt-1 text-sm text-cream/70 font-body">
                      {it.brand ? `${it.brand} · ` : ""}{it.style}{it.origin ? ` · ${it.origin}` : ""}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="chip chip-pink">{it.style}</span>
                    <span className="chip chip-ink">ABV {it.abv}</span>
                    <span className="chip chip-ink">{BUDGET_LABEL[it.budget_tier]}</span>
                    {it.age && it.age !== "Information unavailable" ? (
                      <span className="chip chip-ink">Age {it.age}</span>
                    ) : null}
                  </div>
                  {/* DESCRIPTION */}
                  {it.description ? (
                    <section>
                      <p className="eyebrow">Description</p>
                      <p className="mt-2 text-cream/85 font-body leading-relaxed">{it.description}</p>
                    </section>
                  ) : null}
                  {/* INGREDIENTS / COMPOSITION */}
                  {(it.grape || it.ibu) ? (
                    <section>
                      <p className="eyebrow">Composition</p>
                      <ul className="mt-2 space-y-1 text-sm text-cream/85 font-body">
                        {it.grape ? <li>· Grape: <span className="text-cream">{it.grape}</span></li> : null}
                        {it.body ? <li>· Body: <span className="text-cream">{it.body}</span></li> : null}
                        {it.acidity ? <li>· Acidity: <span className="text-cream">{it.acidity}</span></li> : null}
                        {it.sweetness ? <li>· Sweetness: <span className="text-cream">{it.sweetness}</span></li> : null}
                        {it.ibu ? <li>· IBU: <span className="text-cream">{it.ibu}</span></li> : null}
                      </ul>
                    </section>
                  ) : null}
                  {/* TASTING NOTES */}
                  {it.notes && it.notes.length ? (
                    <section>
                      <p className="eyebrow">Tasting Notes</p>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {it.notes.map(n => <span key={n} className="chip chip-pink text-[10px]">{n}</span>)}
                      </div>
                    </section>
                  ) : null}
                  {/* SERVING */}
                  {it.serving || it.food_pairing ? (
                    <section>
                      <p className="eyebrow">Serving Suggestion</p>
                      <p className="mt-2 text-sm text-cream/85 font-body">{it.serving || it.food_pairing}</p>
                    </section>
                  ) : null}
                  {/* EDITORIAL RATING */}
                  <section>
                    <p className="eyebrow">Editor's Rating</p>
                    <p className="mt-2 text-sm text-cream/85 font-body">
                      <span className="text-pink-brand font-semibold">{it.beginner_rating}/5</span> Beginner Friendliness · Editorial assessment
                    </p>
                  </section>
                  <p className="text-xs text-cream/50 font-body">Prices &amp; availability vary by state. Please verify with a licensed retailer in your area.</p>
                  <div className="flex flex-wrap gap-2">
                    {it.purchase_url ? (
                      <a
                        href={it.purchase_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-pill btn-pink inline-flex"
                        data-testid={`${data.slug}-buy-${it.id}`}
                      >
                        <ShoppingBag size={16} /> Where to Buy
                      </a>
                    ) : (
                      <span
                        className="btn-pill inline-flex opacity-60 cursor-not-allowed border border-cream/20 text-cream/70"
                        data-testid={`${data.slug}-buy-unavailable-${it.id}`}
                      >
                        Where to Buy — Not Available
                      </span>
                    )}
                    {it.official_url ? (
                      <a
                        href={it.official_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-pill btn-outline-ink inline-flex"
                        data-testid={`${data.slug}-official-${it.id}`}
                      >
                        Brand site <ArrowRight size={16} />
                      </a>
                    ) : null}
                  </div>
                  {it.sponsored ? (
                    <p className="text-[10px] uppercase tracking-widest text-pink-brand/80">Sponsored listing</p>
                  ) : null}
                </div>
                </div>
              </DialogContent>
            </Dialog>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <div className="mt-14 text-center">
            <p className="font-serif-display text-2xl text-cream">Nothing here — try a different filter.</p>
          </div>
        )}
      </section>
    </div>
  );
}
